import { type ReactNode, useId } from 'react'
import { StaticCardList, StaticCardListItem } from '@/components/Card'
import { PageSection } from '@/features/jobs/details/components/PageLayout'
import { SectionHeadline } from '@/components/SectionHeadline'
import { Icon } from '@/components/Icon'
import {
  AutoSkeleton,
  SkeletonList,
  useIsSkeleton,
} from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { observer } from 'mobx-react-lite'
import { getQuestionAnswerAsText } from '@/features/jobs/questions/question-display'
import type { QuestionsDisplayViewModel } from '@/features/jobs/details/QuestionsDisplayViewModel'
import styles from './InlineResponsesSection.module.css'

export interface InlineResponsesSectionProps {
  children?: ReactNode
  title?: string
  testId?: string
}

/**
 * The InlineResponsesSection
 */
export function InlineResponsesSection({
  children,
  title = 'Your responses',
  testId,
}: InlineResponsesSectionProps) {
  const headlineId = useId()
  return (
    <PageSection testId={testId}>
      <SectionHeadline id={headlineId}>{title}</SectionHeadline>
      <StaticCardList aria-labelledby={headlineId}>{children}</StaticCardList>
    </PageSection>
  )
}

interface InlineResponseProps {
  question?: string
  answer?: string
  isSkeleton?: boolean
  testId?: string
  questionTestId?: string
  answerTestId?: string
}

/**
 * Inline Response
 */
export function InlineResponse({
  question,
  answer,
  isSkeleton,
  testId,
  questionTestId,
  answerTestId,
}: InlineResponseProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const { vary, index } = useSkeletonListContext()
  return (
    <StaticCardListItem testId={testId}>
      <div className={styles.item}>
        <div className={styles.icon}>
          <Icon skeleton={isSkeleton} name="tick" variant="active" />
        </div>
        <div className={styles.content}>
          <p className="text-body-label pb-sm">
            <AutoSkeleton
              isSkeleton={isSkeleton}
              characters={vary(index % 3 === 0 ? 40 : 25)}
            >
              <span data-test={questionTestId}>{question}</span>
            </AutoSkeleton>
          </p>
          <p className="text-headline-sm">
            <AutoSkeleton
              isSkeleton={isSkeleton}
              characters={vary(index % 3 === 0 ? 60 : 20)}
            >
              <span data-test={answerTestId}>{answer}</span>
            </AutoSkeleton>
          </p>
        </div>
      </div>
    </StaticCardListItem>
  )
}

interface InlineResponsesSectionContainerProps {
  vm: QuestionsDisplayViewModel
}

/**
 * Inline Responses Section Container
 */
export const InlineResponsesSectionContainer = observer(
  function InlineResponsesSectionContainer({
    vm,
  }: InlineResponsesSectionContainerProps) {
    if (!vm.questions) {
      return (
        <InlineResponsesSection>
          <SkeletonList count={vm.job.answers.length}>
            <InlineResponse isSkeleton />
          </SkeletonList>
        </InlineResponsesSection>
      )
    }

    return (
      <InlineResponsesSection>
        {vm.questions.map((q) => (
          <InlineResponse
            key={q.id}
            question={q.title}
            answer={getQuestionAnswerAsText(q)}
          />
        ))}
      </InlineResponsesSection>
    )
  },
)
